import React, { useState, useRef } from 'react';
import { COLORS, SPACING } from '../styles/theme';
import { publishPost } from '../lib/posts';
import { seedFile } from '../lib/torrent';
import { recordSignal } from '../lib/signals';

interface Props {
  replyTo?: { pubkey: string; postId: string };
  onPosted?: () => void;
}

export function PostComposer({ replyTo, onPosted }: Props) {
  const [content, setContent] = useState('');
  const [magnetURI, setMagnetURI] = useState<string | undefined>();
  const [mediaType, setMediaType] = useState<'image' | 'audio' | 'video' | undefined>();
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = file.type.startsWith('image/') ? 'image'
      : file.type.startsWith('audio/') ? 'audio'
      : file.type.startsWith('video/') ? 'video'
      : null;
    if (!type) { setError('Unsupported file type'); return; }
    setError(null);
    try {
      const result = await seedFile(file);
      setMagnetURI(result.magnetURI);
      setMediaType(type as 'image' | 'audio' | 'video');
    } catch {
      setError('Failed to seed file');
    }
    e.target.value = '';
  };

  const handleSubmit = async () => {
    const trimmed = content.trim();
    if (!trimmed) return;
    setPosting(true);
    setError(null);
    try {
      await publishPost(trimmed, { magnetURI, mediaType, replyTo });
      if (magnetURI) recordSignal(magnetURI, 'share', magnetURI);
      setContent('');
      setMagnetURI(undefined);
      setMediaType(undefined);
      onPosted?.();
    } catch {
      setError('Failed to publish post');
    } finally {
      setPosting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit();
  };

  return (
    <div style={{ padding: SPACING.lg, borderBottom: `1px solid ${COLORS.border}` }}>
      {replyTo && (
        <div style={{ fontSize: '11px', color: COLORS.textMuted, marginBottom: SPACING.sm, fontFamily: '"IBM Plex Mono", monospace' }}>
          replying to {replyTo.pubkey.slice(0, 8)}…
        </div>
      )}
      <textarea
        value={content}
        onChange={e => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={replyTo ? 'write a reply… (ctrl+enter)' : "what's happening… (ctrl+enter)"}
        rows={3}
        style={{
          width: '100%',
          background: COLORS.panel,
          color: COLORS.text,
          border: `1px solid ${COLORS.border}`,
          padding: SPACING.md,
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '13px',
          resize: 'vertical',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', gap: SPACING.sm, marginTop: SPACING.sm, alignItems: 'center' }}>
        <input ref={fileRef} type="file" accept="image/*,audio/*,video/*" style={{ display: 'none' }} onChange={handleAttach} />
        <button
          onClick={() => fileRef.current?.click()}
          style={{
            background: 'none', border: `1px solid ${COLORS.border}`,
            color: COLORS.textMuted, padding: `${SPACING.xs} ${SPACING.md}`,
            cursor: 'pointer', fontSize: '11px', fontFamily: '"IBM Plex Mono", monospace',
          }}
        >
          attach
        </button>
        {magnetURI && (
          <span style={{ fontSize: '11px', color: COLORS.highlight }}>✓ {mediaType} attached</span>
        )}
        <button
          onClick={handleSubmit}
          disabled={posting || !content.trim()}
          style={{
            marginLeft: 'auto', background: COLORS.accent, color: '#fff',
            border: 'none', padding: `${SPACING.sm} ${SPACING.lg}`,
            cursor: posting || !content.trim() ? 'not-allowed' : 'pointer',
            fontSize: '13px', fontFamily: '"IBM Plex Mono", monospace',
            opacity: posting || !content.trim() ? 0.5 : 1,
          }}
        >
          {posting ? '…' : 'post'}
        </button>
      </div>
      {error && (
        <div style={{ fontSize: '11px', color: COLORS.danger, marginTop: SPACING.xs }}>{error}</div>
      )}
    </div>
  );
}
